import { createFileRoute } from "@tanstack/react-router";
import {
  type ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type RowSelectionState,
  type SortingState,
  useReactTable
} from "@tanstack/react-table";
import PauseIcon from "lucide-react/dist/esm/icons/pause";
import PlayIcon from "lucide-react/dist/esm/icons/play";
import SparklesIcon from "lucide-react/dist/esm/icons/sparkles";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import { Sparkline } from "@/components/chart/sparkline";
import { DataTable } from "@/components/data-table/data-table";
import { DataTablePagination } from "@/components/data-table/data-table-pagination";
import { BulkActionBar, DataTableToolbar } from "@/components/data-table/data-table-toolbar";
import { FilterSearch, FilterSelect } from "@/components/data-table/filter-controls";
import { useDensity } from "@/components/data-table/use-density";
import { PageBody, PageHeader, Panel } from "@/components/shell/page-frame";
import { EmptyState } from "@/components/states/state-views";
import { StatusBadge } from "@/components/status/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { scenariosFixture } from "@/features/operations/mock-data";
import { type ScenariosSearch, scenariosSearchSchema } from "@/features/operations/schemas";
import {
  SCENARIO_STATE_LABEL,
  SCENARIO_STATE_TONE,
  type Scenario
} from "@/features/operations/types";
import { formatCurrency, formatInteger, formatPercent, formatRelative } from "@/lib/format";
import { useNow } from "@/lib/use-now";

export const Route = createFileRoute("/scenarios/")({
  validateSearch: scenariosSearchSchema,
  component: ScenariosPage
});

function ScenariosPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const now = useNow();
  const { density, setDensity } = useDensity();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([{ id: "revenue24h", desc: true }]);

  const update = (patch: Partial<ScenariosSearch>) => {
    void navigate({ search: (previous) => ({ ...previous, ...patch }) });
  };

  const openBuilder = useCallback(
    (id: string) => {
      void navigate({ to: "/scenarios/$scenarioId", params: { scenarioId: id } });
    },
    [navigate]
  );

  const data = useMemo(() => {
    const keyword = search.q.toLowerCase();
    return scenariosFixture.filter((scenario) => {
      if (search.state !== "all" && scenario.state !== search.state) return false;
      if (search.category !== "all" && scenario.category !== search.category) return false;
      if (!keyword) return true;
      return `${scenario.name} ${scenario.trigger} ${scenario.shopDomain} ${scenario.owner}`
        .toLowerCase()
        .includes(keyword);
    });
  }, [search]);

  const columns = useMemo<ColumnDef<Scenario>[]>(
    () => [
      {
        id: "select",
        enableSorting: false,
        enableHiding: false,
        meta: { sticky: true, width: "36px", label: "選択" },
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onCheckedChange={(checked) => table.toggleAllRowsSelected(checked === true)}
            aria-label="すべて選択"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(checked) => row.toggleSelected(checked === true)}
            aria-label={`${row.original.name} を選択`}
          />
        )
      },
      {
        // 判断に必要な列を左に寄せる（原則 6）
        id: "state",
        accessorKey: "state",
        header: "状態",
        meta: { label: "状態", width: "96px" },
        cell: ({ row }) => (
          <StatusBadge tone={SCENARIO_STATE_TONE[row.original.state]}>
            {SCENARIO_STATE_LABEL[row.original.state]}
          </StatusBadge>
        )
      },
      {
        id: "name",
        accessorKey: "name",
        header: "シナリオ",
        meta: { label: "シナリオ名" },
        cell: ({ row }) => (
          <span className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-fg">{row.original.name}</span>
            <span className="truncate text-2xs text-fg-subtle">{row.original.trigger}</span>
          </span>
        )
      },
      {
        id: "category",
        accessorKey: "category",
        header: "分類",
        meta: { label: "分類", width: "96px" },
        cell: ({ row }) => <span className="text-fg-muted">{row.original.category}</span>
      },
      {
        id: "shopDomain",
        accessorKey: "shopDomain",
        header: "ストア",
        meta: { label: "ストア", priority: "medium" },
        cell: ({ row }) => (
          <span className="text-fg-muted">
            {row.original.shopDomain.replace(".myshopify.com", "")}
          </span>
        )
      },
      {
        id: "trend",
        enableSorting: false,
        header: "推移",
        meta: { label: "配信数の推移", width: "72px", priority: "low" },
        cell: ({ row }) => <Sparkline values={row.original.trend} width={56} height={16} />
      },
      {
        id: "deliveries24h",
        accessorKey: "deliveries24h",
        header: "配信数",
        meta: { label: "配信数(24h)", align: "end", width: "88px" },
        cell: ({ row }) => <span className="num">{formatInteger(row.original.deliveries24h)}</span>
      },
      {
        id: "openRate",
        accessorKey: "openRate",
        header: "開封率",
        meta: { label: "開封率", align: "end", width: "80px", priority: "medium" },
        cell: ({ row }) => <span className="num">{formatPercent(row.original.openRate)}</span>
      },
      {
        id: "conversions24h",
        accessorKey: "conversions24h",
        header: "CV",
        meta: { label: "コンバージョン(24h)", align: "end", width: "72px", priority: "medium" },
        cell: ({ row }) => <span className="num">{formatInteger(row.original.conversions24h)}</span>
      },
      {
        id: "revenue24h",
        accessorKey: "revenue24h",
        header: "帰属売上",
        meta: { label: "帰属売上(24h)", align: "end", width: "120px" },
        cell: ({ row }) => (
          <span className="num font-semibold text-fg">
            {formatCurrency(row.original.revenue24h)}
          </span>
        )
      },
      {
        id: "errorRate",
        accessorKey: "errorRate",
        header: "エラー率",
        meta: { label: "エラー率", align: "end", width: "88px" },
        cell: ({ row }) => {
          const { errorRate } = row.original;
          // しきい値を超えたものだけ色を付ける。全部に色を付けると意味が失われる（原則 4）。
          const tone =
            errorRate >= 5 ? "text-critical-fg" : errorRate >= 1 ? "text-warning-fg" : "text-fg";
          return <span className={`num ${tone}`}>{formatPercent(errorRate)}</span>;
        }
      },
      {
        id: "owner",
        accessorKey: "owner",
        header: "担当",
        meta: { label: "担当者", priority: "low" },
        cell: ({ row }) => <span className="text-fg-muted">{row.original.owner}</span>
      },
      {
        id: "lastRunAt",
        accessorFn: (scenario) => new Date(scenario.lastRunAt).getTime(),
        header: "最終実行",
        meta: { label: "最終実行", align: "end", width: "104px", priority: "medium" },
        cell: ({ row }) => (
          <time dateTime={row.original.lastRunAt} className="num text-fg-muted">
            {formatRelative(row.original.lastRunAt, now)}
          </time>
        )
      },
      {
        id: "open",
        enableSorting: false,
        enableHiding: false,
        header: "",
        meta: { label: "操作", width: "72px" },
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => openBuilder(row.original.id)}
            >
              開く
            </Button>
          </div>
        )
      }
    ],
    [now, openBuilder]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection,
      sorting,
      pagination: { pageIndex: search.page - 1, pageSize: search.pageSize }
    },
    getRowId: (scenario) => scenario.id,
    onRowSelectionChange: setRowSelection,
    // 並び替えでページ位置が意味を失うため 1 ページ目に戻す
    onSortingChange: (updater) => {
      setSorting(updater);
      update({ page: 1 });
    },
    onPaginationChange: (updater) => {
      const current = { pageIndex: search.page - 1, pageSize: search.pageSize };
      const next = typeof updater === "function" ? updater(current) : updater;
      update({ page: next.pageIndex + 1, pageSize: next.pageSize });
    },
    // ページ位置は URL が持つ。データ変化での自動リセットは絞り込み側で明示的に行う
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const selectedIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);

  const runBulk = (label: string) => {
    toast.success(`${selectedIds.length}件のシナリオを${label}しました`, {
      action: { label: "元に戻す", onClick: () => toast.info("操作を取り消しました") }
    });
    setRowSelection({});
  };

  return (
    <>
      <PageHeader
        kind="operations"
        title="シナリオ"
        description="自動化フローの稼働状況。停止・劣化しているものを最初に処理する"
        action={
          <Button size="sm" onClick={() => navigate({ to: "/scenarios/new" })}>
            <SparklesIcon aria-hidden="true" />
            シナリオを作成
          </Button>
        }
      />

      <PageBody density="dense">
        <Panel title="シナリオ一覧" count={formatInteger(data.length)} flush>
          <DataTableToolbar table={table} density={density} onDensityChange={setDensity}>
            <FilterSearch
              label="シナリオ・トリガー・担当"
              value={search.q}
              onChange={(value) => update({ q: value, page: 1 })}
              placeholder="シナリオを検索"
            />
            <FilterSelect
              label="状態"
              value={search.state}
              onChange={(value) => update({ state: value, page: 1 })}
              options={[
                { value: "all", label: "すべて" },
                { value: "running", label: "稼働中" },
                { value: "degraded", label: "劣化" },
                { value: "paused", label: "停止" },
                { value: "draft", label: "下書き" }
              ]}
            />
            <FilterSelect
              label="分類"
              value={search.category}
              onChange={(value) => update({ category: value, page: 1 })}
              options={[
                { value: "all", label: "すべて" },
                { value: "顧客起点", label: "顧客起点" },
                { value: "商品起点", label: "商品起点" },
                { value: "AIO", label: "AIO" },
                { value: "AIOps", label: "AIOps" }
              ]}
            />
          </DataTableToolbar>

          <BulkActionBar selectedCount={selectedIds.length} onClear={() => setRowSelection({})}>
            <Button type="button" size="sm" variant="outline" onClick={() => runBulk("再開")}>
              <PlayIcon aria-hidden="true" />
              一括で再開
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={() => runBulk("一時停止")}>
              <PauseIcon aria-hidden="true" />
              一括で一時停止
            </Button>
          </BulkActionBar>

          <DataTable
            table={table}
            density={density}
            maxHeight="calc(100vh - 300px)"
            onRowActivate={(row) => openBuilder(row.original.id)}
            emptyState={
              <EmptyState
                title="条件に一致するシナリオがありません"
                description="絞り込みを解除するか、別の分類を選んでください。新しいシナリオはテンプレートから作成できます。"
                action={
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => update({ q: "", state: "all", category: "all", page: 1 })}
                  >
                    絞り込みを解除
                  </Button>
                }
              />
            }
          />

          <DataTablePagination
            page={search.page}
            pageSize={search.pageSize}
            totalCount={data.length}
            onPageChange={(page) => update({ page })}
            onPageSizeChange={(pageSize) => update({ pageSize, page: 1 })}
          />
        </Panel>
      </PageBody>
    </>
  );
}
